import { useTheme } from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { type SourceLanguage, sourceLanguageFor } from "./source-language.ts";
import { type SourceTokenLines, sourceTokenChildren } from "./source-tokens.tsx";

type Highlight = {
  text: string;
  language: SourceLanguage;
  scheme: "light" | "dark";
  lines: SourceTokenLines;
};

export function SourceCode({ file, text }: { file: { name: string; mediaType: string }; text: string }) {
  const theme = useTheme();
  const language = sourceLanguageFor(file);
  const [held, setHeld] = useState<Highlight | null>(null);
  const highlighted =
    held?.text === text && held.language === language && held.scheme === theme.scheme ? held.lines : null;

  useEffect(() => {
    if (language === "plaintext") return;
    let alive = true;
    void import("./source-highlighter.ts")
      .then(({ highlightSource }) => highlightSource(text, language, theme.scheme))
      .then((lines) => {
        if (alive) setHeld({ text, language, scheme: theme.scheme, lines });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [language, text, theme.scheme]);

  return (
    <pre
      data-testid="file-source"
      data-language={language}
      data-theme={theme.scheme}
      data-highlighted={highlighted ? "true" : "false"}
      data-selectable="true"
      style={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
        margin: 0,
        overflowX: "auto",
        border: `1px solid ${theme.color["border.default"]}`,
        borderRadius: theme.radius.md,
        background: theme.color["bg.surfaceAlt"],
        color: theme.color["text.secondary"],
        padding: theme.space[3],
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: theme.type.caption.size,
        lineHeight: `${theme.type.caption.line}px`,
        whiteSpace: "pre",
        tabSize: 2,
        userSelect: "text",
      }}
    >
      {highlighted ? sourceTokenChildren(highlighted) : text}
    </pre>
  );
}
